import { v4 as uuidv4 } from 'uuid';
import { ParsedEmailOrder, extractOriginalOrderSection } from './emailParser';
import { ParsedOrderData, ReviewQueueItem, FlagReason } from '../types/reviewQueue';
import { Order } from '../types/order';
import { Client, IndividualClient } from '../types/client';

export interface ValidationResult {
  isValid: boolean;
  flagReason?: FlagReason;
  flagDetails?: string;
}

/**
 * Validates parsed email data to determine if it can be auto-created
 * or needs to go to review queue
 */
export const validateParsedEmail = (
  parsedEmail: ParsedEmailOrder,
  availableRecipes?: { id: string; name: string }[]
): ValidationResult => {
  const { customer, items } = parsedEmail;

  // Check for missing customer name
  if (!customer.name || customer.name.trim() === '') {
    return {
      isValid: false,
      flagReason: 'missing-customer-name',
      flagDetails: 'Customer name is required but was not found in the email'
    };
  }

  // Check for valid email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!customer.email || !emailRegex.test(customer.email)) {
    return {
      isValid: false,
      flagReason: 'invalid-email',
      flagDetails: `Email "${customer.email}" is not valid or is missing`
    };
  }

  // Check for phone (basic validation - should have digits)
  if (!customer.phone || !/\d{3,}/.test(customer.phone)) {
    return {
      isValid: false,
      flagReason: 'invalid-phone',
      flagDetails: `Phone "${customer.phone}" is not valid or is missing`
    };
  }

  // Check for address components
  if (!customer.address.street || customer.address.street.trim() === '') {
    return {
      isValid: false,
      flagReason: 'missing-address',
      flagDetails: 'Street address is required but was not found'
    };
  }

  if (!customer.address.city || customer.address.city.trim() === '') {
    return {
      isValid: false,
      flagReason: 'missing-address',
      flagDetails: 'City is required but was not found'
    };
  }

  if (!customer.address.province || customer.address.province.trim() === '') {
    return {
      isValid: false,
      flagReason: 'missing-address',
      flagDetails: 'Province is required but was not found'
    };
  }

  if (!customer.address.postalCode || customer.address.postalCode.trim() === '') {
    return {
      isValid: false,
      flagReason: 'missing-address',
      flagDetails: 'Postal code is required but was not found'
    };
  }

  // Check for at least one order item
  if (!items || items.length === 0) {
    return {
      isValid: false,
      flagReason: 'missing-items',
      flagDetails: 'At least one order item is required'
    };
  }

  // Check for unmatched recipes (if available recipes are provided)
  if (availableRecipes && availableRecipes.length > 0) {
    const unmatchedItems = items.filter(item => {
      if (item.quantity <= 0) return false; // Skip items with no quantity

      // Try to find a matching recipe (case-insensitive)
      const matchedRecipe = availableRecipes.find(recipe => recipe.name.toLowerCase() === item.name.toLowerCase());

      return !matchedRecipe; // Item is unmatched if no recipe found
    });

    if (unmatchedItems.length > 0) {
      const unmatchedNames = unmatchedItems.map(item => item.name).join(', ');
      return {
        isValid: false,
        flagReason: 'unmatched-recipe',
        flagDetails: `The following items could not be matched to known recipes: ${unmatchedNames}`
      };
    }
  }

  // Validation passed
  return { isValid: true };
};

/**
 * Checks for duplicate orders from the same customer email within the last N days
 */
export const checkForDuplicateOrder = (
  email: string,
  existingOrders: Order[],
  daysToCheck: number = 7
): { isDuplicate: boolean; duplicateOrder?: Order } => {
  const normalizedEmail = email.trim().toLowerCase();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysToCheck);

  for (const order of existingOrders) {
    if (order.email.trim().toLowerCase() === normalizedEmail) {
      const orderDate = new Date(order.orderDate);
      if (orderDate >= cutoffDate) {
        return { isDuplicate: true, duplicateOrder: order };
      }
    }
  }

  return { isDuplicate: false };
};

/**
 * Converts ParsedEmailOrder to ParsedOrderData format for review queue
 */
export const convertToReviewQueueData = (parsedEmail: ParsedEmailOrder): ParsedOrderData => {
  const { customer, items } = parsedEmail;

  return {
    customerName: customer.name,
    email: customer.email,
    phone: customer.phone,
    streetAddress: customer.address.street,
    addressLine2: '', // Email parser doesn't extract this separately
    city: customer.address.city,
    province: customer.address.province,
    postalCode: customer.address.postalCode,
    orderDate: new Date().toISOString().split('T')[0], // Today's date
    deliveryType: 'Delivery', // Default to delivery
    items: items.map(item => ({
      recipeName: item.name,
      quantity: item.quantity
    }))
  };
};

/**
 * Creates a review queue item from parsed email and validation result
 */
export const createReviewQueueItem = (
  parsedEmail: ParsedEmailOrder,
  flagReason: FlagReason,
  flagDetails?: string
): ReviewQueueItem => {
  // Extract the original "Your Order" section for human reference
  const originalOrderText = extractOriginalOrderSection(parsedEmail.rawEmail.body);

  return {
    id: uuidv4(),
    parsedData: convertToReviewQueueData(parsedEmail),
    rawEmail: parsedEmail.rawEmail.body,
    originalOrderText: originalOrderText || undefined,
    flagReason,
    flagDetails,
    receivedAt: parsedEmail.rawEmail.receivedAt || new Date().toISOString(),
    flaggedAt: new Date().toISOString()
  };
};

/**
 * Finds or creates a client for the given email/phone
 * Returns clientId (existing or newly generated)
 */
export const findOrPrepareClient = (
  email: string,
  phone: string,
  existingClients: Client[]
): { clientId: string; isExisting: boolean; existingClient?: Client } => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPhone = phone.trim();

  // Search for existing client
  const existingClient = existingClients.find(client => {
    const clientEmail = client.email?.trim().toLowerCase();
    const clientPhone = client.phone?.trim();

    if (normalizedEmail && clientEmail && normalizedEmail === clientEmail) {
      return true;
    }

    if (normalizedPhone && clientPhone && normalizedPhone === clientPhone) {
      return true;
    }

    return false;
  });

  if (existingClient) {
    return {
      clientId: existingClient.id,
      isExisting: true,
      existingClient
    };
  }

  // Generate new client ID
  return {
    clientId: uuidv4(),
    isExisting: false
  };
};

/**
 * Creates a new client from parsed order data
 */
export const createClientFromParsedData = (parsedData: ParsedOrderData, clientId: string): IndividualClient => {
  const fullAddress = [
    parsedData.streetAddress,
    parsedData.addressLine2,
    `${parsedData.city}, ${parsedData.province} ${parsedData.postalCode}`
  ]
    .filter(Boolean)
    .join(', ');

  return {
    id: clientId,
    type: 'individual',
    name: parsedData.customerName,
    email: parsedData.email,
    phone: parsedData.phone,
    address: fullAddress,
    notes: `Auto-created from email order on ${new Date().toLocaleDateString()}`,
    preferences: '',
    createdAt: new Date().toISOString()
  };
};

/**
 * Creates an order from parsed data
 */
export const createOrderFromParsedData = (
  parsedData: ParsedOrderData,
  clientId: string,
  availableRecipes: { id: string; name: string }[],
  originalOrderText?: string
): Order => {
  // Map recipe names to recipe IDs
  const orderItems = parsedData.items.map(item => {
    const recipe = availableRecipes.find(r => r.name.toLowerCase() === item.recipeName.toLowerCase());
    return {
      id: uuidv4(),
      recipeId: recipe?.id || '',
      recipeName: item.recipeName,
      quantity: item.quantity
    };
  });

  return {
    id: uuidv4(),
    customerName: parsedData.customerName,
    customerType: 'Individual',
    email: parsedData.email,
    phone: parsedData.phone,
    streetAddress: parsedData.streetAddress,
    addressLine2: parsedData.addressLine2,
    city: parsedData.city,
    province: parsedData.province,
    postalCode: parsedData.postalCode,
    orderDate: parsedData.orderDate,
    deliveryType: parsedData.deliveryType,
    items: orderItems,
    status: 'Pending',
    isFinalized: false,
    clientId,
    ...(originalOrderText && { originalOrderText })
  };
};

/**
 * Main processor: validates email and determines action
 * Returns either a valid order data or a review queue item
 */
export interface ProcessingResult {
  action: 'auto-create' | 'review-queue';
  parsedData?: ParsedOrderData;
  reviewQueueItem?: ReviewQueueItem;
  flagReason?: FlagReason;
  flagDetails?: string;
}

export const processEmailOrder = (
  parsedEmail: ParsedEmailOrder,
  existingOrders: Order[],
  availableRecipes?: { id: string; name: string }[]
): ProcessingResult => {
  // Step 1: Validate email data
  const validation = validateParsedEmail(parsedEmail, availableRecipes);

  if (!validation.isValid) {
    // Failed validation - send to review queue
    return {
      action: 'review-queue',
      reviewQueueItem: createReviewQueueItem(parsedEmail, validation.flagReason!, validation.flagDetails),
      flagReason: validation.flagReason,
      flagDetails: validation.flagDetails
    };
  }

  // Step 2: Check for duplicate
  const duplicateCheck = checkForDuplicateOrder(parsedEmail.customer.email, existingOrders, 7);

  if (duplicateCheck.isDuplicate) {
    // Found duplicate - send to review queue
    const duplicateOrder = duplicateCheck.duplicateOrder!;
    return {
      action: 'review-queue',
      reviewQueueItem: createReviewQueueItem(
        parsedEmail,
        'duplicate',
        `Possible duplicate: existing order #${duplicateOrder.id.slice(0, 8)} from ${new Date(
          duplicateOrder.orderDate
        ).toLocaleDateString()}`
      ),
      flagReason: 'duplicate',
      flagDetails: 'Duplicate order detected within last 7 days'
    };
  }

  // Step 3: Validation passed and no duplicate - ready for auto-creation
  return {
    action: 'auto-create',
    parsedData: convertToReviewQueueData(parsedEmail)
  };
};
